import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { dequal } from 'dequal';
import {
  QueryParamConfig,
  QueryParamConfigMap,
  QueryParamOptions,
  useQueryParam,
  useQueryParams,
} from 'use-query-params';

// https://github.com/sandiiarov/use-deep-compare
export function useDeepCompareEffect(
  effect: React.EffectCallback,
  dependencies: React.DependencyList
) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(effect, useDeepCompareMemoize(dependencies));
}

const useDeepCompareMemoize = (dependencies: React.DependencyList) => {
  const dependenciesRef = useRef<React.DependencyList>(dependencies);
  const signalRef = useRef<number>(0);

  if (!dequal(dependencies, dependenciesRef.current)) {
    dependenciesRef.current = dependencies;
    signalRef.current += 1;
  }

  return useMemo(
    () => dependenciesRef.current,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [signalRef.current]
  );
};

export const useDeepCompareMemo = <T>(
  factory: () => T,
  dependencies: React.DependencyList
) => {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(factory, useDeepCompareMemoize(dependencies));
};

export const useInterval = (callback: () => void, delayInMs: number) => {
  const callbackRef = useRef<() => void>();

  useEffect(() => {
    callbackRef.current = callback;
  });

  useEffect(() => {
    if (delayInMs !== null) {
      const interval = setInterval(
        () => callbackRef.current?.(),
        delayInMs || 0
      );
      return () => clearInterval(interval);
    }

    return undefined;
  }, [delayInMs]);
};

export const useKampusQueryParam = <
  TypeToEncode,
  TypeFromDecode = TypeToEncode,
>(
  name: string,
  paramConfig?: QueryParamConfig<TypeToEncode, TypeFromDecode>,
  options?: QueryParamOptions
) => {
  const newOptions: QueryParamOptions = {
    updateType: 'replaceIn',
    ...options,
  };

  return useQueryParam<TypeToEncode, TypeFromDecode>(
    name,
    paramConfig,
    newOptions
  );
};

export const useKampusQueryParams = <QPCMap extends QueryParamConfigMap>(
  paramConfigMap: QPCMap,
  options?: QueryParamOptions
) => {
  const newOptions: QueryParamOptions = {
    updateType: 'replaceIn',
    ...options,
  };

  return useQueryParams<QPCMap>(paramConfigMap, newOptions);
};

export const usePrevious = <T>(value: T) => {
  const ref = useRef<T>();

  useEffect(() => {
    ref.current = value;
  });

  return ref.current;
};

export const useDisclosure = (
  initialValue = false
): [
  boolean,
  {
    open: () => void;
    close: () => void;
    toggle: () => void;
  },
] => {
  const [state, setState] = useState(initialValue);

  const open = useCallback(() => setState(true), []);
  const close = useCallback(() => setState(false), []);
  const toggle = useCallback(() => setState((prev) => !prev), []);

  return [
    state,
    {
      toggle: toggle,
      open: open,
      close: close,
    },
  ];
};

/**
 * Utility to log changed dependencies in useEffect to the console.
 */
export const useEffectDebugger = <T extends Array<unknown>>(
  dependencies: T,
  label = '[use-effect-debugger] ',
  effectHook: React.EffectCallback = () => {
    /* Do Nothing */
  },
  dependencyNames = []
) => {
  const previousDeps = usePrevious(dependencies);

  const changedDeps = dependencies.reduce(
    (accum: T, dependency: unknown, index: number) => {
      // eslint-disable-next-line
      // @ts-ignore
      const previousDep = previousDeps?.[index];

      if (dependency !== previousDep) {
        const keyName = dependencyNames[index] || index;
        return {
          ...accum,
          [keyName]: {
            before: previousDep,
            after: dependency,
          },
        };
      }

      return accum;
    },
    {} as T
  );

  if (Object.keys(changedDeps).length) {
    // eslint-disable-next-line no-console
    console.log(label, changedDeps);
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(effectHook, dependencies);
};

export function usePreviousLocation() {
  const location = useLocation();

  const previousLocationRef = useRef<typeof location | null>(null);

  useEffect(() => {
    previousLocationRef.current = location;
  }, [location]);

  return previousLocationRef.current;
}

export function useSnackbarNotifications() {
  const [notifications, setNotifications] = useState<
    { id: string; message: ReactNode }[]
  >([]);

  const notify = useCallback((message: ReactNode): string => {
    const id = crypto.randomUUID();
    setNotifications((prev) => [...prev, { id, message }]);

    return id;
  }, []);

  const dismiss = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const replace = useCallback((oldId: string, message: ReactNode): string => {
    const newId = crypto.randomUUID();
    setNotifications((prev) =>
      prev.filter((n) => n.id !== oldId).concat({ id: newId, message })
    );

    return newId;
  }, []);

  return { notifications, notify, dismiss, replace } as const;
}
